import React, { useState, useEffect, lazy, Suspense } from 'react';
import { storageService } from './services/storageService';

const SAVED_KEY = 'handmadebee_saved_v1';
import Navbar from './components/Navbar';
import Hero3D from './components/Hero3D';
import AtelierStrip from './components/AtelierStrip';
import ThreadDivider from './components/ThreadDivider';
import WorksShelves from './components/WorksShelves';
import BeforeAfterViewer from './components/BeforeAfterViewer';
import Faq from './components/Faq';
import Footer from './components/Footer';

// Split out of the initial bundle. None of these are on screen when the page
// loads, and the admin panel in particular is dead weight for every visitor
// who is not the studio — it shipped in the same file as the hero.
const LightboxModal = lazy(() => import('./components/LightboxModal'));
const QuoteEstimator = lazy(() => import('./components/QuoteEstimator'));
const AdminLoginModal = lazy(() => import('./components/Admin/AdminLoginModal'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const WorkUploadModal = lazy(() => import('./components/Admin/WorkUploadModal'));
const CloudSyncGuideModal = lazy(() => import('./components/Admin/CloudSyncGuideModal'));

export default function App() {
  const [works, setWorks] = useState([]);
  const [activeView, setActiveView] = useState('showcase'); // 'showcase' | 'admin'
  const [isAdminAuth, setIsAdminAuth] = useState(false);

  // Filter & Search State

  // Modals
  const [selectedLightboxWork, setSelectedLightboxWork] = useState(null);
  const [selectedBeforeAfterWork, setSelectedBeforeAfterWork] = useState(null);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [quotePreset, setQuotePreset] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState(null);
  const [isCloudGuideOpen, setIsCloudGuideOpen] = useState(false);

  // Load works from storage
  useEffect(() => {
    const loadedWorks = storageService.getWorks();
    setWorks(loadedWorks);
  }, []);

  // A hash in the URL is resolved by the browser before `works` has loaded, so
  // the target section does not exist yet and the scroll silently does nothing.
  // Re-run it once the sections that depend on data have actually mounted.
  useEffect(() => {
    if (!works.length || !window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth' }));
  }, [works.length]);

  // The admin panel has no entry point in the customer-facing UI. It opens at
  // /admin (or #admin), which keeps the studio's back office out of a
  // visitor's field of view without hiding it from the studio.
  useEffect(() => {
    const check = () => {
      const wanted =
        window.location.pathname.replace(/\/+$/, '') === '/admin' ||
        window.location.hash === '#admin';
      // Route through the same handler as any other entry so an already
      // authenticated session goes straight to the dashboard.
      if (wanted) handleOpenAdmin();
    };
    check();
    window.addEventListener('hashchange', check);
    return () => window.removeEventListener('hashchange', check);
  }, []);

  // Calculate work counts per category
  // The before/after section renders nothing unless a work has both flags,
  // so the nav must not offer a link to it in that case.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SAVED_KEY);
      if (raw) setSavedIds(JSON.parse(raw));
    } catch {
      // unreadable storage just means nothing is saved yet
    }
  }, []);

  const showTransformations = works.some((w) => w.hasBeforeAfter && w.beforeImage);

  // The order form is the one thing on this page a visitor is meant to open,
  // and it now lives in its own chunk. Fetch it once the browser is idle so
  // the click is instant rather than paying for the round trip.
  useEffect(() => {
    const warm = () => { import('./components/QuoteEstimator'); };
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(warm, { timeout: 4000 })
      : window.setTimeout(warm, 2500);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, []);

  // Admin Handlers
  const handleOpenAdmin = () => {
    if (isAdminAuth) {
      setActiveView('admin');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    setIsAdminAuth(true);
    setActiveView('admin');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveWork = (workPayload) => {
    if (editingWork) {
      const updated = storageService.updateWork(editingWork.id, workPayload);
      setWorks(storageService.getWorks());
      setEditingWork(null);
    } else {
      const created = storageService.saveWork(workPayload);
      setWorks(storageService.getWorks());
    }
  };

  const handleEditWork = (work) => {
    setEditingWork(work);
    setIsUploadModalOpen(true);
  };

  const handleDeleteWork = (id) => {
    storageService.deleteWork(id);
    setWorks(storageService.getWorks());
  };

  const handleToggleFeatured = (id) => {
    const item = works.find(w => w.id === id);
    if (item) {
      storageService.updateWork(id, { featured: !item.featured });
      setWorks(storageService.getWorks());
    }
  };

  const handleResetDefaults = () => {
    if (window.confirm('Reset all works back to the initial Handmade bee catalog?')) {
      const reset = storageService.resetToDefaults();
      setWorks(reset);
    }
  };

  // Saved pieces live in this browser and nowhere else. The previous version
  // incremented an invented `likes` count that was never displayed and that
  // the gallery could be sorted by — a fake number driving a real control.
  const toggleSave = (id) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        window.localStorage.setItem(SAVED_KEY, JSON.stringify(next));
      } catch {
        // private mode, or storage full — saving is a convenience, never a
        // reason to break the page
      }
      return next;
    });
  };

  // Opens the order form already pointed at this piece, instead of jumping
  // straight to WhatsApp with no name, no contact and no lead recorded.
  const handleEnquire = (work) => {
    setQuotePreset({ craft: work.category, piece: work.title });
    setIsQuoteOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF2EB] text-[#4E2C23] relative">
      
      {/* Navigation */}
      <Navbar
        activeView={activeView}
        setActiveView={setActiveView}
        onOpenQuote={() => setIsQuoteOpen(true)}
        showTransformations={showTransformations}
      />

      {/* Main View Router */}
      {activeView === 'admin' && isAdminAuth ? (
        <Suspense fallback={<div className="min-h-screen" />}>
          <AdminDashboard
            works={works}
            onAddNew={() => { setEditingWork(null); setIsUploadModalOpen(true); }}
            onEdit={handleEditWork}
            onDelete={handleDeleteWork}
            onToggleFeatured={handleToggleFeatured}
            onResetDefaults={handleResetDefaults}
            onBackToShowcase={() => { setActiveView('showcase'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            onOpenCloudGuide={() => setIsCloudGuideOpen(true)}
            onPreviewWork={(work) => setSelectedLightboxWork(work)}
          />
        </Suspense>
      ) : (
        <main>
          {/* 3D Mouse Reactive Hero */}
          <Hero3D
            onExplore={() => {
              const el = document.getElementById('showcase');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            onOpenQuote={() => setIsQuoteOpen(true)}
          />

          {/* Three-panel atelier strip */}
          <AtelierStrip
            onOpenQuote={() => setIsQuoteOpen(true)}
            onExplore={() => {
              const el = document.getElementById('showcase');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          />

          {/* Scroll-drawn thread between the strip and the work */}
          <ThreadDivider />

          {/* Master Works Gallery Section */}
          <section id="showcase" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-10 sm:space-y-12">
            
            {/* Section masthead — left aligned, rule under it. Deliberately not
                the centred eyebrow-pill stack used elsewhere; three identical
                section headers in a row is what makes a page feel templated. */}
            <div className="border-b border-[#4E2C23]/15 pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="space-y-2">
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#7F6055] font-bold block">
                  Portfolio
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-90s font-black text-[#4E2C23] tracking-tight leading-[1.05]">
                  Recent work
                </h2>
              </div>
              <p className="text-sm text-[#745147] max-w-sm md:text-right leading-relaxed">
                Embroidery hoops, painted art, framed calligraphy, gift hampers and
                embroidered kids’ wear — every piece made by hand, to order.
              </p>
            </div>

            {/* Category Filter & Search Bar */}
            {/* Gallery Grid */}
            <WorksShelves
              works={works}
              onOpenLightbox={(work) => setSelectedLightboxWork(work)}
              savedIds={savedIds}
              onToggleSave={toggleSave}
            />

          </section>

          {/* Interactive Before & After Slider */}
          <BeforeAfterViewer
            works={works}
            selectedWork={selectedBeforeAfterWork}
          />

          <Faq onOpenQuote={() => setIsQuoteOpen(true)} />

        </main>
      )}

      {/* Footer */}
      <Footer
        onOpenQuote={() => setIsQuoteOpen(true)}
        showTransformations={showTransformations}
      />

      {/* Modals & Dialogs. Mounted only while open so their chunks are never
          fetched by a visitor who does not open them. */}
      <Suspense fallback={null}>
        {selectedLightboxWork && (
          <LightboxModal
            work={selectedLightboxWork}
            allWorks={works}
            onClose={() => setSelectedLightboxWork(null)}
            onSelectWork={(w) => setSelectedLightboxWork(w)}
            onEnquire={handleEnquire}
            onBrowseCategory={(category) => {
              setSelectedLightboxWork(null);
              const el = document.getElementById(`shelf-${category}`);
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        )}

        {isQuoteOpen && (
          <QuoteEstimator
            isOpen
            presetCraft={quotePreset?.craft}
            presetPiece={quotePreset?.piece}
            onClose={() => { setIsQuoteOpen(false); setQuotePreset(null); }}
          />
        )}

        {isLoginModalOpen && (
          <AdminLoginModal
            isOpen
            onClose={() => setIsLoginModalOpen(false)}
            onLoginSuccess={handleLoginSuccess}
          />
        )}

        {isUploadModalOpen && (
          <WorkUploadModal
            isOpen
            onClose={() => { setIsUploadModalOpen(false); setEditingWork(null); }}
            onSave={handleSaveWork}
            editingWork={editingWork}
          />
        )}

        {isCloudGuideOpen && (
          <CloudSyncGuideModal isOpen onClose={() => setIsCloudGuideOpen(false)} />
        )}
      </Suspense>

    </div>
  );
}
